import { createContext, useContext } from 'react';
import cn from 'classnames';

import OPE from 'js/on-page-editing';
import { defineTheme, type ThemeKeys } from 'js/utils/theme-helpers';

import ContentAreaItem from 'components/content-area-item/ContentAreaItem';

import { AdditionalComponentProps } from 'source/component-registry';
import { ContentAreaItemAdditionalProps } from 'components/content-area-item/ContentAreaItem';

import { IContentArea } from 'source/NFR.types';

const MAX_NESTING_DEPTH = 10;
const ContentAreaDepthContext = createContext(0);

const themes = defineTheme({
  singleColumn: 'content-area--theme-single-column',
  twoColumns: 'content-area--theme-two-columns',
  threeColumns: 'content-area--theme-three-columns',
  fourColumns: 'content-area--theme-four-columns',
  portfolio: 'content-area--theme-portfolio'
});
export type ContentAreaTheme = ThemeKeys<typeof themes>;

/*
NOTE: additionalComponentProps can be used to send props to all components of a specific type.
This only applies to the components rendered directly by ContentAreaItem (in other words these props don't trickle all the way down the render tree)

Example: forcing the 'blue' theme for all InfoBlocks rendered by a content area:
   <ContentArea additionalComponentProps={{ InfoBlock: { theme: "blue" } }} />
*/

/*
NOTE: additionalItemProps works in much the same way as 'additionalComponentProps', except for the fact that it controls the ContentAreaItem wrapper instead of the component itself.

Example: forcing full width for all wrappers around InfoBlocks rendered by a content area:
  <ContentArea additionalItemProps={{ InfoBlock: { size: ContentAreaItem.sizes.fullScreen } }} />
*/

// NOTE: enableElementSizing controls both this component and ContentAreaItem. When set to true, ContentArea uses flexbox. When set to false it doesn't, and ContentAreaItems are always rendered full width.

export interface ContentAreaProps extends IContentArea {
  additionalComponentProps?: AdditionalComponentProps;
  additionalItemProps?: ContentAreaItemAdditionalProps;
  className?: string;
  enableElementSizing?: boolean;
  theme?: ContentAreaTheme | ContentAreaTheme[];
}

const ContentArea: React.FC<ContentAreaProps> = ({
  additionalComponentProps = {},
  additionalItemProps = {},
  enableElementSizing = true,
  blocks = [],
  blockNotSupportedText,
  className,
  onPageEditing,
  theme
}) => {
  const depth = useContext(ContentAreaDepthContext);

  if (depth >= MAX_NESTING_DEPTH) {
    return null;
  }

  if (blocks.length === 0 && !onPageEditing?.name) {
    return null;
  }

  return (
    <ContentAreaDepthContext.Provider value={depth + 1}>
      <div
        className={cn('content-area', { 'content-area--flex-layout': enableElementSizing }, themes(theme), className)}
        {...OPE(onPageEditing?.name)}
      >
        {blocks?.map(block => (
          <ContentAreaItem
            additionalComponentProps={additionalComponentProps}
            blockNotSupportedText={blockNotSupportedText}
            enableElementSizing={enableElementSizing}
            isStaticLayout={!!theme}
            key={block.id}
            {...block}
            {...additionalItemProps[block.componentName]}
          />
        ))}
      </div>
    </ContentAreaDepthContext.Provider>
  );
};

ContentArea.displayName = 'ContentArea';

export default ContentArea;
